import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LifeBuoy, Loader2 } from 'lucide-react';
import { PERMISSIONS } from '@asyagrup/group-hub-contracts';
import { useSession } from '@/app/session-context';
import { IconButton } from '@/ui/primitives';
import { CreateTicketDialog } from './CreateTicketDialog';
import { captureScreen } from './capture';
import { destekKayitEkraninda } from './destek-sekmesi';

/**
 * Report a problem from the screen the problem is on.
 *
 * The ticket already had a `pageUrl` field and it could not be trusted: to
 * reach Destek you had to LEAVE the broken screen, so the field that exists to
 * answer "hangi ekranda?" recorded `/destek`. `CreateTicketDialog` worked
 * around it by reading the same-origin referrer — a good guess, and still a
 * guess. Opened from here, the address is not inferred at all: it is where the
 * reporter is standing.
 *
 * The screenshot is taken BEFORE the dialog opens, for the same reason: half a
 * second later the sheet covers the thing being reported.
 *
 * It is attached, not sent. The capture appears in the file list like any other
 * attachment and can be removed there — which matters on a system whose screens
 * carry salaries and national ids. Automatic is not the same as silent.
 */
export function ReportButton() {
  const { can } = useSession();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [shot, setShot] = useState<File[]>([]);
  const [page, setPage] = useState('');

  if (!can(PERMISSIONS.SUPPORT_CREATE)) return null;

  async function start() {
    setBusy(true);
    /*
     * DESTEK SAYFASININ KENDİSİ FOTOĞRAFLANMIYOR — DST-00078.
     *
     * Kuyruktaki dört kayıt /destek'ten açılmıştı ve dördünün de eki destek
     * listesinin görüntüsüydü: sorunu değil, sorunu bildirdiği sayfayı
     * gösteriyordu. Burada ne adres ne görüntü bir şey anlatıyor; adres boş
     * bırakılınca pencere geldiği ekranı tarayıcının yönlendireninden okuyor,
     * görüntü yerine de sorunu yaşanan ekrandan nasıl bildirileceğini söylüyor.
     *
     * Kural yalnız DESTEK KAYITLARI yarısı için. Aynı adresteki Yenilikler
     * sekmesi başka bir ekran: orada bir sürüm notu yanlış görünüyorsa görüntüsü
     * sorunun kendisi, ve adresi (`?sekme=yenilikler`) hangi ekran olduğunu
     * söylüyor. Kararı `destekKayitEkraninda` veriyor — sayfanın kendi sekme
     * kararıyla aynı fonksiyon.
     */
    const destekte = destekKayitEkraninda(
      window.location.pathname,
      window.location.search,
      can(PERMISSIONS.SUPPORT_READ_OWN),
    );
    // Read the address first: the capture is asynchronous and a click on a link
    // mid-capture would otherwise file the ticket against the next screen.
    setPage(destekte ? '' : window.location.pathname + window.location.search);
    const file = destekte ? null : await captureScreen();
    setShot(file ? [file] : []);
    setBusy(false);
    setOpen(true);
  }

  return (
    <>
      <IconButton
        aria-label="Sorun bildir"
        title="Bu ekranla ilgili sorun bildir"
        onClick={() => void start()}
        disabled={busy}
        data-no-screenshot
      >
        {busy ? <Loader2 size={18} className="animate-spin" /> : <LifeBuoy size={18} />}
      </IconButton>

      {open && (
        <CreateTicketDialog
          initialKind="BUG"
          initialPageUrl={page || undefined}
          initialFiles={shot}
          canManage={can(PERMISSIONS.SUPPORT_MANAGE)}
          onClose={() => setOpen(false)}
          onOpenDetail={(id) => {
            setOpen(false);
            navigate(`/destek?kayit=${id}`);
          }}
          onAnnounce={() => {
            // The dialog announces to its own live region while it is open.
          }}
          onNotice={() => {
            // The receipt lives in the dialog; the header has no room for a
            // trace and Destek shows the ticket anyway.
          }}
        />
      )}
    </>
  );
}
